// ======================================================================
// File: src/articles/CareerGuide.jsx
// Article: How to Choose the Right Course and Career Path in Nigeria
// ======================================================================

import { Typography, Divider } from "@mui/material";


export default function CareerGuide() {
  return (
    <>
      {/* INTRO */}
      <Typography sx={{ mb: 2 }}>
        Choosing the right course and career path is one of the most important
        decisions a student will ever make. Unfortunately, many students in
        Nigeria choose courses based on pressure from parents, trends on social
        media, or the choices of friends — and this often leads to frustration,
        failure, or a career they do not enjoy.
      </Typography>

      <Typography sx={{ mb: 3 }}>
        This guide explains how to choose a course based on your strengths,
        interests, personality, job opportunities, and Nigeria’s current
        employment realities. Whether you are preparing for WAEC, NECO, or JAMB,
        this will help you make a smarter, more confident career decision.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* SECTION 1 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        1. Understand Your Strengths and Natural Abilities
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Every student has natural strengths. The biggest mistake is choosing a
        course that does not fit your strongest subjects.
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>If you enjoy calculations → Engineering, Finance, Economics</li>
        <li>If you enjoy reading → Law, Mass Communication, International Relations</li>
        <li>If you love science → Medicine, Pharmacy, Nursing, Biology-based courses</li>
        <li>If you are creative → Architecture, Arts, Design, Theatre</li>
        <li>If you enjoy leading others → Business Admin, Public Admin, HR</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Your WAEC and school performance already shows what you are naturally
        good at — don’t ignore it.
      </Typography>

      {/* SECTION 2 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        2. Know Your Personality Type
      </Typography>

      <Typography sx={{ mt: 2 }}>
        The best career choices match your personality.  
        Ask yourself:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Am I introverted or extroverted?</li>
        <li>Do I prefer practical work or theory?</li>
        <li>Do I enjoy working alone or in a team?</li>
        <li>Do I like routines or flexible environments?</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        For example:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Introverts → Programming, Accounting, Writing, Engineering</li>
        <li>Extroverts → Law, Marketing, Mass Communication, Teaching</li>
        <li>Hands-on learners → Engineering, Nursing, Agriculture</li>
        <li>Theoretical thinkers → Law, Philosophy, Political Science</li>
      </Typography>

      {/* SECTION 3 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        3. Understand Nigeria’s Job Market Reality
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Some courses naturally have more opportunities in Nigeria than others.
        Before choosing a course, understand employment trends.
      </Typography>

      <Typography sx={{ mt: 2, fontWeight: 700 }}>
        Courses with strong job demand:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Nursing, Medicine & Health Sciences</li>
        <li>Computer Science & Software Engineering</li>
        <li>Cybersecurity</li>
        <li>Engineering (Electrical, Mechanical, Civil)</li>
        <li>Finance & Accounting</li>
        <li>Data Science</li>
        <li>Digital Marketing</li>
      </Typography>

      <Typography sx={{ mt: 2, fontWeight: 700 }}>
        Courses with limited opportunities:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Certain pure sciences without specialization</li>
        <li>Some education courses (depending on region)</li>
        <li>Political Science and Sociology (high competition)</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        This does NOT mean they are bad courses — it simply means you must have a
        clear plan for specialization or skills development.
      </Typography>

      {/* SECTION 4 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        4. Match Courses With Subject Combinations
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Many students lose admission because they choose the wrong JAMB subject
        combination. Every course has specific requirements.
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Law → English, Literature, CRS/IRS, Government</li>
        <li>Medicine → English, Biology, Chemistry, Physics</li>
        <li>Engineering → English, Mathematics, Physics, Chemistry</li>
        <li>Mass Communication → English, Literature, Government</li>
        <li>Computer Science → English, Maths, Physics, Chemistry</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Before choosing a course, always check the JAMB brochure or trusted
        subject combination guides.
      </Typography>

      {/* SECTION 5 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        5. Research the Course Before Choosing It
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Many students choose a course without understanding what they will study.
        This leads to frustration later.
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Check the major topics</li>
        <li>Find out the practical requirements</li>
        <li>Understand the job roles it leads to</li>
        <li>Know the skills required</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Example: Computer Science is not just “using a laptop” — it requires
        strong logical reasoning and mathematics.
      </Typography>

      {/* SECTION 6 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        6. Consider the University Offering the Course
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Some universities are better at some courses than others.
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Engineering → UNILAG, UI, FUTA, UNN</li>
        <li>Medicine → UNILORIN, UI, UNIBEN</li>
        <li>Law → UNILAG, OAU, ABU</li>
        <li>Computer Science → FUTA, LASU, UNILAG</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Check cut-off marks, reputation, and facilities before applying.
      </Typography>

      {/* SECTION 7 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        7. Think Long-Term (Future Opportunities)
      </Typography>

      <Typography sx={{ mt: 2 }}>
        A good course should give you future flexibility. Ask:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Does this course allow specialization?</li>
        <li>Can I work remotely with this skill?</li>
        <li>Is the job market growing?</li>
        <li>Can I start a business with this skill?</li>
      </Typography>

      {/* ENDING */}
      <Divider sx={{ my: 4 }} />

      <Typography sx={{ mb: 2 }}>
        Choosing a course is not about following friends or pleasing parents. It
        is about understanding yourself, knowing your strengths, and choosing a
        path that gives you the best chance for success. Take time to research,
        ask questions, and think long-term.
      </Typography>

      <Typography>
        With the right decision today, you will build a future you can be proud
        of.
      </Typography>
	  
	    

    </>
  );
}
